import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search, Users, GraduationCap, Award, Dumbbell, Lock, Car, Trophy } from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  { key: "deportes", label: "Deportes", icon: Trophy },
  { key: "gimnasio", label: "Gimnasio", icon: Dumbbell },
  { key: "locker", label: "Locker", icon: Lock },
  { key: "estacionamiento", label: "Estacionamiento", icon: Car },
];

export default function AdminBuscarFamilia() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) { toast.error("Escribe al menos 2 caracteres."); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/members/search`, { params: { q: q.trim() } });
      setData(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };

  const toggleBenefit = async (fam, mIdx, key) => {
    const m = fam.members[mIdx];
    if (!m.benefits?.rut_hash) { toast.error("Esta persona no tiene RUT para asignar beneficios."); return; }
    const next = { ...m.benefits, [key]: !m.benefits[key] };
    setSaving(`${m.benefits.rut_hash}-${key}`);
    setData((prev) => {
      const copy = structuredClone(prev);
      const f = copy.families.find((x) => x.rut === fam.rut);
      if (f) f.members[mIdx].benefits = next;
      return copy;
    });
    try {
      await api.put(`/admin/members/${m.benefits.rut_hash}/benefits`, {
        deportes: next.deportes, locker: next.locker,
        estacionamiento: next.estacionamiento, gimnasio: next.gimnasio,
      });
    } catch (err) {
      toast.error("No se pudo guardar el beneficio.");
      setData((prev) => {
        const copy = structuredClone(prev);
        const f = copy.families.find((x) => x.rut === fam.rut);
        if (f) f.members[mIdx].benefits = m.benefits;
        return copy;
      });
    } finally { setSaving(null); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center gap-3 mb-2">
        <Search className="w-7 h-7 text-accent" />
        <h1 className="text-2xl font-bold">Buscar familia</h1>
      </div>
      <p className="text-muted-foreground mb-5">Busca por apellido, nombre, RUT o correo. Verás a toda la familia con sus datos, condición de socio, beca y beneficios.</p>

      <form onSubmit={search} className="flex gap-3 max-w-xl mb-6">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ej.: Guendelman, 9.001.871-K o correo@…" className="h-11 rounded-xl" data-testid="family-search-input" />
        <Button type="submit" disabled={loading} className="btn-lift h-11 rounded-xl px-6" data-testid="family-search-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Buscar"}
        </Button>
      </form>

      {data && data.families.length === 0 && (
        <div className="bg-white rounded-2xl border border-border p-10 text-center text-muted-foreground" data-testid="family-empty">Sin resultados para “{q}”.</div>
      )}

      <div className="space-y-5">
        {data?.families.map((fam) => (
          <div key={fam.rut || fam.titular_nombre} className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="family-card">
            <div className="bg-secondary/50 px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="flex items-center gap-2 font-semibold text-primary"><Users className="w-4 h-4" /> {fam.titular_nombre}</span>
              <span className="text-xs text-muted-foreground">{fam.categoria || "—"} · {fam.comuna || "—"}</span>
              <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${fam.vigente === "Sí" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>Socio {fam.vigente === "Sí" ? "activo" : "no vigente"}</span>
              {fam.is_ih && <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-blue-100 text-blue-800 flex items-center gap-1"><GraduationCap className="w-3 h-3" /> IH</span>}
            </div>
            <div className="divide-y divide-border">
              {fam.members.map((m, i) => (
                <div key={i} className="px-5 py-3 flex flex-col lg:flex-row lg:items-center gap-3" data-testid="family-member-row">
                  <div className="lg:w-1/3">
                    <p className="font-medium">{m.nombre} <span className="text-xs text-muted-foreground">· {m.rol}</span></p>
                    <p className="text-xs text-muted-foreground">{m.rut || "sin RUT"} {m.email ? `· ${m.email}` : ""} {m.telefono ? `· ${m.telefono}` : ""}</p>
                  </div>
                  <div className="lg:w-1/4 text-xs">
                    {m.beca_actual ? <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><Award className="w-3 h-3" /> Beca {m.beca_actual.year}: {m.beca_actual.pct}%</span>
                      : m.beca_historica ? <span className="text-slate-500">Última beca {m.beca_historica.year}: {m.beca_historica.pct}%</span>
                      : <span className="text-slate-400">Sin beca</span>}
                  </div>
                  <div className="flex flex-wrap gap-2 lg:flex-1 lg:justify-end">
                    {BENEFITS.map(({ key, label, icon: Icon }) => {
                      const on = !!m.benefits?.[key];
                      const disabled = !m.benefits?.rut_hash || saving === `${m.benefits?.rut_hash}-${key}`;
                      return (
                        <button key={key} type="button" disabled={disabled}
                          onClick={() => toggleBenefit(fam, i, key)}
                          className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full border px-3 py-1.5 transition-colors disabled:opacity-40 ${on ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-border hover:bg-secondary/50"}`}
                          data-testid={`benefit-${key}-${m.benefits?.rut_hash || i}`}>
                          <Icon className="w-3.5 h-3.5" /> {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
